import { type LinksFunction, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from '@remix-run/react';
import { ThemeModeScript, ThemeProvider } from 'flowbite-react';
import { useChangeLanguage } from 'remix-i18next/react';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';
import { HoneypotProvider } from 'remix-utils/honeypot/react';
import { Toaster } from 'sonner';
import { GenericErrorBoundary } from '#common/components/ErrorBoundary.tsx';
import { StepUpProvider } from '#common/features/auth/StepUp/context.tsx';
import { validateEditMode } from '#common/i18n/edit-mode.server.ts';
import { EditModeProvider } from '#common/i18n/edit-mode.tsx';
import { localeCookie } from '#common/i18n/i18n.server';
import { crispyvoiceTheme } from '#common/theme.ts';
import { ConsentHandling } from '#common/utils/consent-handling.js';
import { serializeCookieByConsent } from '#common/utils/consent.server.js';
import { honeypot } from '#common/utils/honeypot.server';
import { getUrlWithoutLang } from '#common/utils/i18n.tsx';
import { getLocale, getMetaTitle } from '#speaker-admin/i18n/i18n.server';
import { getUser, getImpersonatedUserId, isImpersonating } from '#speaker-admin/session.server';
import '#speaker-admin/styles/app.css';
import { ImpersonationBanner } from './components/ImpersonationBanner.tsx';
import { MediumsIconLoader } from './utils/mediums-icon-loader.tsx';
import { environment, PublicEnv } from './utils/public-env';

export const handle = { i18n: [ 'common', 'navigation', 'auth', 'login' ] };

export async function loader(args: LoaderFunctionArgs) {
    const { request } = args;
    const user = await getUser(request);
    const locale = await getLocale(user, request);
    const honeyProps = await honeypot.getInputProps();
    const env = environment();

    return typedjson(
        {
            locale,
            title: await getMetaTitle(request),
            honeyProps,
            user,
            env,
            baseUrl: getUrlWithoutLang(request.url),
            isEditModeValid: validateEditMode(request),
            isImpersonating: await isImpersonating(request),
            impersonatedUserId: await getImpersonatedUserId(request)
        },
        { headers: { 'Set-Cookie': await serializeCookieByConsent(request, 'functionality', localeCookie, locale) } }
    );
}

export default function App() {
    const { locale, honeyProps, env, isImpersonating: impersonating, impersonatedUserId, user } = useTypedLoaderData<
        typeof loader
    >();
    useChangeLanguage(locale);

    return (
        <EditModeProvider routeId='root'>
            <HoneypotProvider {...honeyProps}>
                <ThemeProvider theme={crispyvoiceTheme}>
                    <html
                        lang={locale ?? 'en'}
                        className='h-full scroll-smooth no-touchevents antialiased'
                        translate='no'
                        suppressHydrationWarning
                    >
                        <head>
                            <meta charSet='utf-8' />
                            <meta name='viewport' content='width=device-width, initial-scale=1' />
                            <ConsentHandling />
                            <Meta />
                            <Links />
                            <ThemeModeScript />
                            <PublicEnv {...env} />
                        </head>
                        <body className='h-full w-full scroll-smooth'>
                            <ImpersonationBanner
                                isImpersonating={impersonating}
                                impersonatedUserId={impersonatedUserId}
                                userName={user?.speaker?.name}
                            />
                            <StepUpProvider>
                                <main className='h-full w-full'>
                                    <Outlet />
                                </main>
                                <Toaster richColors position='top-center' />
                                <ScrollRestoration />
                                <Scripts />
                                <MediumsIconLoader />
                            </StepUpProvider>
                        </body>
                    </html>
                </ThemeProvider>
            </HoneypotProvider>
        </EditModeProvider>
    );
}

export const links: LinksFunction = () => {
    return [ { rel: 'apple-touch-icon', sizes: '180x180', href: '/favicons/apple-touch-icon.png' }, {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicons/favicon-32x32.png'
    }, { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicons/favicon-16x16.png' } ];
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
    return [ { title: data?.title ?? 'Error' } ];
};

export function ErrorBoundary() {
    return (
        <html lang='en' className='h-full scroll-smooth antialiased'>
            <head>
                <meta charSet='utf-8' />
                <meta name='viewport' content='width=device-width, initial-scale=1' />
                <Meta />
                <Links />
            </head>
            <body className='h-full w-full'>
                <GenericErrorBoundary homePath='/' />
                <Scripts />
            </body>
        </html>
    );
}
